import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { Restoration } from 'models/order';
import TypographyWithHighLighting from 'components/with-hith-light-components/typography-with-highlighting.component';
import { displayProperties, formatPropertyValue, NUMBER_OF_UNITS_KEY, propertyByKey } from './restoration-format';

type RestorationPropertiesProps = {
    restoration: Restoration;
    originalRestoration?: Restoration;
};

/**
 * Unit counts first (as the legacy section showed them), then the property grid.
 * Changes are highlighted against the same property on the original order — by key,
 * so a property added later does not shift the comparison of the ones after it.
 */
const RestorationProperties: FC<RestorationPropertiesProps> = ({ restoration, originalRestoration }) => {
    const { t: translate } = useTranslation(['orders', 'construction-props']);
    const units = propertyByKey(restoration, NUMBER_OF_UNITS_KEY)?.units ?? [];
    const originalUnits = propertyByKey(originalRestoration, NUMBER_OF_UNITS_KEY)?.units ?? [];
    const properties = displayProperties(restoration);

    return (
        <Fragment>
            {units.length ? (
                <Box mb={2}>
                    <Grid container spacing={1}>
                        {units.map((unit, unitIndex) => (
                            <Grid item key={`${unit.actionRowId}-${unitIndex}`} xs={12} lg={6} xl={4}>
                                <strong>{unit.label}:&nbsp;</strong>
                                <TypographyWithHighLighting
                                    data={`${unit.value}`}
                                    originalData={`${
                                        originalUnits.find((original) => original.label === unit.label)?.value ?? ''
                                    }`}
                                    variant="body2"
                                    color="#555"
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : undefined}
            {properties.length ? (
                <Box mb={2}>
                    <Grid container spacing={1}>
                        {properties.map((property) => (
                            <Grid item key={property.key} xs={12} lg={4} xl={4}>
                                <strong>
                                    {translate(`orders:${property.key}`, { defaultValue: property.key })}:&nbsp;
                                </strong>
                                <TypographyWithHighLighting
                                    data={formatPropertyValue(property, translate)}
                                    originalData={formatPropertyValue(
                                        propertyByKey(originalRestoration, property.key),
                                        translate,
                                    )}
                                    variant="body2"
                                    color="#555"
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default RestorationProperties;
